import React, { memo, useMemo, useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Smile, CheckCheck, Sparkles, Loader2, Trash2 } from 'lucide-react';
import { cn } from "../../lib/utils";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import EmojiPicker, { Theme } from 'emoji-picker-react';
import { useTheme } from '../theme-provider';
import { useGlobalState } from '../../contexts/GlobalStateContext';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { toast } from 'sonner';

interface AccountTextInputProps {
  label: string;
  text: string;
  tiktokTitle?: string;
  checked: boolean;
  disabled: boolean;
  uploading: boolean;
  onTextChange: (text: string) => void;
  onTiktokTitleChange?: (title: string) => void;
  onCheckedChange: (checked: boolean) => void;
  onRemove?: () => void;
  placeholder: string;
  maxCharacters?: number;
  captions?: string[]; // Array of generated captions
  currentCaptionIndex?: number; // Current caption index
  onCaptionChange?: (index: number) => void; // Callback when caption index changes
  hideCheckbox?: boolean; // Explicitly hide checkbox if controlled by platforms
  language?: string;
  onGenerateCaptions?: () => void;
  isGeneratingCaptions?: boolean;
  showTiktokTitle?: boolean;
  onTranslate?: (targetLangs: string[]) => void;
  isTranslating?: boolean;
  isBaseLanguage?: boolean;
}

const AccountTextInput = memo(function AccountTextInput({
  label,
  text,
  tiktokTitle = '',
  checked,
  disabled,
  uploading,
  onTextChange,
  onTiktokTitleChange,
  onCheckedChange,
  onRemove,
  placeholder,
  maxCharacters,
  captions = [],
  currentCaptionIndex = -1,
  onCaptionChange,
  hideCheckbox = false,
  language = 'en',
  onGenerateCaptions,
  isGeneratingCaptions = false,
  showTiktokTitle = false,
  onTranslate,
  isTranslating = false,
  isBaseLanguage = false,
}: AccountTextInputProps): JSX.Element {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { theme } = useTheme();
  const { user } = useGlobalState();
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isCheckingGrammar, setIsCheckingGrammar] = useState(false);
  const [isRephrasing, setIsRephrasing] = useState(false);
  const [fixedText, setFixedText] = useState<string | null>(null);
  const prevIsTranslating = useRef(isTranslating);

  // Reset fixedText when text is manually changed
  // We only reset if we are NOT currently translating/fixing, to avoid race conditions
  useEffect(() => {
    if (!isTranslating && !isCheckingGrammar && !isRephrasing && text !== fixedText) {
      setFixedText(null);
    }
  }, [text, fixedText, isTranslating, isCheckingGrammar, isRephrasing]);

  // When translation finishes, the current text is now considered "fixed"
  useEffect(() => {
    if (prevIsTranslating.current && !isTranslating && text) {
      setFixedText(text);
    }
    prevIsTranslating.current = isTranslating;
  }, [isTranslating, text]);

  const getLanguageFlag = (lang: string) => {
    const flags: Record<string, string> = {
      'en': '🇺🇸',
      'es': '🇪🇸',
      'fr': '🇫🇷',
      'pt': '🇵🇹',
      'de': '🇩🇪',
      'it': '🇮🇹',
      'ja': '🇯🇵',
      'zh': '🇨🇳',
      'ru': '🇷🇺',
      'ko': '🇰🇷',
      'br': '🇧🇷',
    };
    return flags[lang.toLowerCase()] || '🏳️';
  };

  const charactersLeft = useMemo(() =>
    maxCharacters !== undefined ? maxCharacters - text.length : undefined,
    [maxCharacters, text.length]
  );
  const isOverLimit = useMemo(() =>
    charactersLeft !== undefined && charactersLeft < 0,
    [charactersLeft]
  );

  const hasCaptions = captions.length > 0;
  const canGoPrev = hasCaptions && currentCaptionIndex > 0;
  const canGoNext = hasCaptions && currentCaptionIndex < captions.length - 1;

  const handlePrev = () => {
    if (canGoPrev && onCaptionChange) {
      onCaptionChange(currentCaptionIndex - 1);
    }
  };

  const handleNext = () => {
    if (canGoNext && onCaptionChange) {
      onCaptionChange(currentCaptionIndex + 1);
    }
  };

  const handleGrammarCheck = async () => {
    if (!text.trim()) return;
    setIsCheckingGrammar(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/ai/grammar-check`, {
        text,
        language
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      onTextChange(response.data.correctedText);
      setFixedText(response.data.correctedText);
      toast.success('Grammar & spelling fixed!');
    } catch (error: any) {
      console.error('Grammar check failed:', error);
      toast.error(error.response?.data?.error || 'Failed to check grammar');
    } finally {
      setIsCheckingGrammar(false);
    }
  };

  const handleRephrase = async () => {
    if (!text.trim()) return;
    setIsRephrasing(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/ai/rephrase`, {
        text,
        language,
        style: 'engaging'
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      onTextChange(response.data.rephrasedText);
      setFixedText(response.data.rephrasedText);
      toast.success('Text rephrased!');
    } catch (error: any) {
      console.error('Rephrase failed:', error);
      toast.error(error.response?.data?.error || 'Failed to rephrase text');
    } finally {
      setIsRephrasing(false);
    }
  };

  const onEmojiClick = (emojiData: any) => {
    if (!textareaRef.current) return;

    const start = textareaRef.current.selectionStart;
    const end = textareaRef.current.selectionEnd;
    const emoji = emojiData.emoji;

    const newText = text.substring(0, start) + emoji + text.substring(end);
    onTextChange(newText);

    // Set focus and move cursor back after emoji insertion
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(start + emoji.length, start + emoji.length);
      }
    }, 0);
  };

  return (
    <div className={cn(
      "space-y-3 p-4 rounded-2xl transition-all duration-300",
      isBaseLanguage ? "bg-primary/5 border border-primary/20 shadow-lg shadow-primary/5" : "bg-muted/20 border border-border/50"
    )}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {!hideCheckbox && (
            <Checkbox
              id={`check-${label}`}
              checked={checked}
              onCheckedChange={(v: boolean) => onCheckedChange(v)}
              disabled={uploading}
              className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
            />
          )}
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="text-sm">{getLanguageFlag(language)}</span>
              <Label
                htmlFor={`check-${label}`}
                className={cn(
                  "text-foreground font-black uppercase tracking-widest text-[10px] cursor-pointer select-none",
                  !checked && "text-muted-foreground"
                )}
              >
                {label}
              </Label>
              {isBaseLanguage && (
                <Badge className="bg-primary/20 text-primary border-primary/30 text-[8px] h-4 font-black uppercase tracking-tighter px-1.5">Source</Badge>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {hasCaptions && hideCheckbox && (
            <div className="flex items-center gap-2 bg-card/50 p-1 rounded-lg border border-border">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={handlePrev}
                disabled={!canGoPrev || uploading}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-[10px] text-muted-foreground font-mono min-w-[40px] text-center">
                {currentCaptionIndex + 1} / {captions.length}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={handleNext}
                disabled={!canGoNext || uploading}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          {onRemove && !isBaseLanguage && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-red-400 hover:bg-red-400/10"
              onClick={onRemove}
              disabled={uploading}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {showTiktokTitle && onTiktokTitleChange && (
          <div className="space-y-1.5">
            <Label className="text-[9px] font-black text-muted-foreground uppercase tracking-widest ml-1">TikTok Video Title</Label>
            <Input
              value={tiktokTitle}
              onChange={(e) => onTiktokTitleChange(e.target.value)}
              disabled={disabled || !checked}
              placeholder="Give your TikTok a catchy title..."
              className="h-9 bg-background border-border text-foreground placeholder:text-muted-foreground focus:ring-primary text-xs font-bold"
              maxLength={80}
            />
          </div>
        )}

        <div className="relative group">
          <Textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            disabled={disabled || !checked}
            className={cn(
              "min-h-[100px] bg-background border-border text-foreground placeholder:text-muted-foreground focus:ring-primary resize-none transition-all pr-12 text-sm leading-relaxed",
              isOverLimit && "border-red-500 focus:ring-red-500",
              !checked && "opacity-40 grayscale pointer-events-none"
            )}
            placeholder={placeholder}
          />

          {checked && !disabled && (
            <div className="absolute right-3 bottom-2 flex items-center gap-2">
              <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                    title="Insert emoji"
                  >
                    <Smile className="h-5 w-5" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 border-none bg-transparent shadow-2xl" align="end" side="top">
                  <EmojiPicker
                    theme={theme === 'dark' ? Theme.DARK : Theme.LIGHT}
                    onEmojiClick={onEmojiClick}
                    autoFocusSearch={false}
                    searchPlaceHolder="Search emoji..."
                    width={320}
                    height={400}
                  />
                </PopoverContent>
              </Popover>

              {maxCharacters !== undefined && (
                <div className={cn(
                  "text-[10px] font-bold px-1.5 py-0.5 rounded backdrop-blur-sm",
                  isOverLimit
                    ? "text-red-400 bg-red-500/10 border border-red-500/20"
                    : "text-muted-foreground bg-muted/50"
                )}>
                  {charactersLeft}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {checked && !disabled && (
        <div className="flex flex-wrap items-center gap-2 pt-1 animate-in fade-in slide-in-from-top-1 duration-300">
          {onGenerateCaptions && (
            <Button
              variant="outline"
              size="sm"
              onClick={onGenerateCaptions}
              disabled={isGeneratingCaptions || isCheckingGrammar || isRephrasing || uploading}
              className="h-7 px-3 bg-indigo-600/5 border-indigo-500/20 text-indigo-400 hover:bg-indigo-600/10 text-[10px] font-bold uppercase tracking-wider gap-1.5 shadow-sm"
            >
              {isGeneratingCaptions ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              AI Captions
            </Button>
          )}

          {user?.featureFlags?.grammar_check && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleGrammarCheck}
              disabled={isCheckingGrammar || isRephrasing || !text.trim() || uploading || text === fixedText}
              className="h-7 px-3 bg-primary/5 border-primary/20 text-primary hover:bg-primary/10 text-[10px] font-bold uppercase tracking-wider gap-1.5 shadow-sm"
            >
              {isCheckingGrammar ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCheck className="h-3 w-3" />}
              Fix Grammar
            </Button>
          )}

          {user?.featureFlags?.rephrase && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRephrase}
              disabled={isCheckingGrammar || isRephrasing || !text.trim() || uploading || text === fixedText}
              className="h-7 px-3 bg-purple-600/5 border-purple-500/20 text-purple-400 hover:bg-purple-600/10 text-[10px] font-bold uppercase tracking-wider gap-1.5 shadow-sm"
            >
              {isRephrasing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              Rephrase
            </Button>
          )}
        </div>
      )}
    </div>
  );
});

export default AccountTextInput;

